import { useState, type ReactNode } from "react";
import Button from "@/components/ui/Button";
import { ConfirmContext, type ConfirmOptions } from "./useConfirm";

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(
    null,
  );

  const confirm = (opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    return new Promise((resolve) => {
      setResolver(() => resolve);
    });
  };

  const handleConfirm = () => {
    if (resolver) resolver(true);
    setOptions(null);
  };

  const handleCancel = () => {
    if (resolver) resolver(false);
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {options && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {options.title}
            </h3>
            <p className="mt-2 text-gray-600">{options.message}</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button onClick={handleCancel} variant="secondary">
                {options.cancelText ?? "Cancel"}
              </Button>
              <Button onClick={handleConfirm} variant="destructive">
                {options.confirmText ?? "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { useConfirm } from "./useConfirm";
